import { useState } from 'react';
import { map } from 'rxjs/operators';
import { modal, ModalLayout, ModalTemplate, confirm } from '@ardoq/modal';
import {
  copyConfigurationTo,
  saveConfigurationsFailure,
  saveConfigurationsSuccess,
} from 'integrations/common/streams/transferConfigs/actions';
import { SavedTransferConfig } from '@ardoq/api-types/integrations';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { Select } from '@ardoq/select';
import { allIntegrations } from 'integrations/allIntegrations';
import { isIntegrationWithTransferConfigs } from 'integrations/common/streams/transferConfigs/utils';
import { TextInput } from '@ardoq/forms';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { OverviewRoute } from 'integrations/common/navigation/types';
import { byLocaleField } from 'integrations/common/utils/common';
import { hasFeature } from '@ardoq/features';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { toIntegrationLocation } from 'integrations/router/utils';

type CopyToTransferConfigModalProps = {
  onSubmit: (integrationId: IntegrationId) => void;
  onCancel: VoidFunction;
  config: SavedTransferConfig;
  integrationId: IntegrationId;
  i18nLocale: I18nLocale;
};

const CopyToTransferConfigModalComponent = ({
  onSubmit,
  onCancel,
  config,
  integrationId,
  i18nLocale,
}: CopyToTransferConfigModalProps) => {
  const [selectedIntegrationId, setSelectedIntegrationId] =
    useState<IntegrationId>();
  const [name, setName] = useState(config.name);
  const [isCopying, setIsCopying] = useState(false);
  const activeIntegrations = allIntegrations.filter(
    integration =>
      isIntegrationWithTransferConfigs(integration.id as IntegrationId) &&
      (integration.requiredFeatures ?? []).every(hasFeature) &&
      integration.id !== integrationId
  );

  const handleCopyConfig = async () => {
    if (!selectedIntegrationId) {
      return;
    }
    setIsCopying(true);
    const action = await dispatchActionAndWaitForResponse(
      copyConfigurationTo({
        integrationId,
        name: name.trim(),
        config,
        to: selectedIntegrationId,
      }),
      saveConfigurationsSuccess,
      saveConfigurationsFailure
    );
    setIsCopying(false);

    if (action.type === saveConfigurationsSuccess.type) {
      onSubmit(selectedIntegrationId);
    }
  };

  return (
    <ModalTemplate
      headerText={t(
        i18nLocale,
        'integrations:copyToTransferConfigModal.headerText',
        'Copy configuration?'
      )}
      primaryButtonText={t(
        i18nLocale,
        'integrations:copyToTransferConfigModal.primaryButtonText',
        'Copy configuration'
      )}
      secondaryButtonText={t(
        i18nLocale,
        'integrations:copyToTransferConfigModal.secondaryButtonText',
        'Cancel'
      )}
      isPrimaryButtonDisabled={!selectedIntegrationId}
      onPrimaryButtonClick={handleCopyConfig}
      onSecondaryButtonClick={onCancel}
      isInProgress={isCopying}
      modalSize={2}
    >
      <ModalLayout>
        <TextInput
          label={t(
            i18nLocale,
            'integrations:copyToTransferConfigModal.nameLabel',
            'Name of copy'
          )}
          onValueChange={setName}
          value={name}
          placeholder={t(
            i18nLocale,
            'integrations:copyToTransferConfigModal.namePlaceholder',
            'Enter name for the copied configuration'
          )}
        />
        <Select
          label={t(
            i18nLocale,
            'integrations:copyToTransferConfigModal.copyToLabel',
            'Copy to'
          )}
          value={selectedIntegrationId}
          placeholder={t(
            i18nLocale,
            'integrations:copyToTransferConfigModal.copyToPlaceholder',
            'Select an integration'
          )}
          onValueChange={val =>
            val && setSelectedIntegrationId(val as IntegrationId)
          }
          options={activeIntegrations
            .map(i => ({
              label: i.name,
              value: i.id,
            }))
            .sort(byLocaleField('label'))}
          i18nLocale={i18nLocale}
        />
      </ModalLayout>
    </ModalTemplate>
  );
};

const handleCopyConfirmation = async (
  i18nLocale: I18nLocale,
  targetIntegration: IntegrationId
) => {
  const openIntegration = await confirm({
    title: t(
      i18nLocale,
      'integrations:copyToTransferConfigModal.copiedConfirmTitle',
      'Configuration copied'
    ),
    subtitle: t(
      i18nLocale,
      'integrations:copyToTransferConfigModal.copiedConfirmSubtitle',
      'Do you want to open the integration?'
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'integrations:copyToTransferConfigModal.copiedConfirmYes',
      'Yes'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'integrations:copyToTransferConfigModal.copiedConfirmNo',
      'No'
    ),
  });
  if (openIntegration) {
    dispatchAction(
      navigate(
        toIntegrationLocation({
          id: targetIntegration,
          path: OverviewRoute.CONFIGURATIONS,
        })
      )
    );
  }
};

type CopyToTransferConfigModalContainerProps = {
  resolve: (value: boolean) => void;
  config: SavedTransferConfig;
  integrationId: IntegrationId;
  i18nLocale: I18nLocale;
};

const CopyToTransferConfigModalContainer = ({
  resolve,
  config,
  integrationId,
  i18nLocale,
}: CopyToTransferConfigModalContainerProps) => (
  <CopyToTransferConfigModalComponent
    onSubmit={async targetIntegration => {
      await handleCopyConfirmation(i18nLocale, targetIntegration);
      resolve(true);
    }}
    onCancel={() => resolve(false)}
    config={config}
    integrationId={integrationId}
    i18nLocale={i18nLocale}
  />
);

const viewModel$ = i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })));

const CopyToTransferConfigModal = connect(
  CopyToTransferConfigModalContainer,
  viewModel$
);

export const startCopyToTransferConfigModal = (
  integrationId: IntegrationId,
  config: SavedTransferConfig
) =>
  modal<boolean>(
    resolve => (
      <CopyToTransferConfigModal
        resolve={resolve}
        config={config}
        integrationId={integrationId}
      />
    ),
    {
      autoFocus: true,
      shouldCloseOnEscapeKey: true,
      shouldCloseOnBackdropClick: false,
    }
  );
